/** The host fits every add-on image in a 1024 px square, so nothing larger is worth sending. */
const ADD_ON_IMAGE_MAX_SIDE = 1024;

/** What the file picker offers, and all the reduction decodes. */
export const IMAGE_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

interface Encoding {
    type: string;
    extension: string;
    quality?: number;
}

// Any source but a JPEG becomes a PNG, so transparency survives.
const JPEG: Encoding = { type: 'image/jpeg', extension: 'jpg', quality: 0.85 };
const PNG: Encoding = { type: 'image/png', extension: 'png' };

/** The browser could not decode the image, or could not encode its scaled copy. */
export class UnreadableAddOnImage extends Error {
    constructor(file: File) {
        super(`Unable to read the image file: ${file.name}`);
        this.name = 'UnreadableAddOnImage';
    }
}

/** A file that already fits travels untouched, so a small GIF keeps its animation. */
export async function reduceAddOnImage(file: File): Promise<File> {
    // The picker's accept list is only a hint, and the host would refuse the file after the upload.
    if (!IMAGE_FILE_TYPES.includes(file.type)) throw new UnreadableAddOnImage(file);

    const bitmap = await decode(file);
    // A browser that cannot turn the photo sends it whole, and the host fits and turns it.
    if (!bitmap) return file;

    try {
        const scale = ADD_ON_IMAGE_MAX_SIDE / Math.max(bitmap.width, bitmap.height);
        if (scale >= 1) return file;

        const encoding = file.type === JPEG.type ? JPEG : PNG;
        const blob = await encodeScaled(bitmap, scale, encoding);
        if (!blob) throw new UnreadableAddOnImage(file);

        return new File([blob], `${stemOf(file.name)}.${encoding.extension}`, { type: encoding.type });
    } finally {
        bitmap.close();
    }
}

// A phone stores a photo as the sensor saw it, with a tag saying how to turn it,
// and a browser that predates the option refuses it outright.
async function decode(file: File): Promise<ImageBitmap | null> {
    // Safari 14 has no decoder to ask.
    if (typeof createImageBitmap !== 'function') return null;

    try {
        return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (error) {
        if (error instanceof TypeError) return null;
        throw new UnreadableAddOnImage(file);
    }
}

function encodeScaled(bitmap: ImageBitmap, scale: number, encoding: Encoding): Promise<Blob | null> {
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d');
    if (!context) return Promise.resolve(null);

    // The default smoothing leaves a large downscale jagged.
    context.imageSmoothingQuality = 'high';
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    return new Promise(resolve =>
        canvas.toBlob(
            blob => {
                // WebKit keeps a canvas's pixels until it is resized to nothing.
                canvas.width = canvas.height = 0;
                resolve(blob);
            },
            encoding.type,
            encoding.quality
        )
    );
}

// The name the operator knows the image by, less the extension the new encoding replaces.
const stemOf = (name: string): string => name.replace(/\.[^.]*$/, '');
